import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, CircularProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import DocxViewer, { FIT_SCALE } from "bundles/Internal/components/common/DocxViewer";

// An A4 page at 100% zoom, in CSS pixels (595pt at 96dpi). Used to turn the pane width into a
// zoom percentage, so the PDF lands at the same size as a DOCX rendered by docx-preview.
const PAGE_WIDTH_PX = 794;
const ZOOM_STEP = 3;

// The CV itself, beside the form. PDFs render in the browser's own viewer; a DOCX is rendered by
// docx-preview when the caller opts in with `isDocx`, and the text pane is always the fallback.
const CvViewer = ({
  fileUrl,
  isPdf,
  isDocx = false,
  cvText,
  filename,
  tab: controlledTab,
  onTabChange,
  hideHeader = false,
  fitWidth = false,
  textPending = false,
  textPendingLabel = "Reading the document...",
}) => {
  const [internalTab, setInternalTab] = useState("document");
  const [zoom, setZoom] = useState(null);
  const paneRef = useRef(null);
  const tab = controlledTab || internalTab;
  const setTab = onTabChange || setInternalTab;
  const showDocument = Boolean(fileUrl) && (isPdf || isDocx);
  const active = showDocument ? tab : "text";

  useLayoutEffect(() => {
    const el = paneRef.current;
    if (!fitWidth || isDocx || !el) return undefined;

    const compute = () => {
      const width = el.clientWidth;
      if (!width) return;

      const next = Math.round(((width * FIT_SCALE) / PAGE_WIDTH_PX) * 100);
      setZoom((prev) => (prev === null || Math.abs(prev - next) >= ZOOM_STEP ? next : prev));
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);

    return () => observer.disconnect();
  }, [fitWidth, isDocx]);

  const documentSrc =
    fitWidth && fileUrl && zoom ? `${fileUrl}#zoom=${zoom}&toolbar=0&navpanes=0` : fileUrl;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {!hideHeader && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, borderBottom: "1px solid hsl(140 8% 88%)", backgroundColor: "hsl(140 10% 95%)", flexShrink: 0 }}
        >
          <Tabs
            value={active}
            onChange={(_e, next) => setTab(next)}
            sx={{
              minHeight: 38,
              "& .MuiTab-root": {
                minHeight: 38, textTransform: "none", fontSize: 12, px: 1.5,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)",
                "&.Mui-selected": { color: "hsl(150 38% 17%)" },
              },
              "& .MuiTabs-indicator": { backgroundColor: "hsl(150 38% 17%)" },
            }}
          >
            {showDocument && <Tab value="document" label="Document" />}
            <Tab value="text" label="Extracted text" disabled={!cvText} />
          </Tabs>
          <Typography noWrap sx={{ fontSize: 11, color: "hsl(150 10% 46%)", maxWidth: 180 }}>
            {filename}
          </Typography>
        </Stack>
      )}

      <Box
        ref={paneRef}
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", backgroundColor: "hsl(140 6% 92%)" }}
      >
        {active === "document" && isDocx && <DocxViewer fileUrl={fileUrl} />}
        {active === "document" && !isDocx && (
          <Box component="embed" src={documentSrc} type="application/pdf" sx={{ width: "100%", height: "100%" }} />
        )}
        {active === "text" && !cvText && textPending && (
          <Stack
            spacing={1.5}
            sx={{ height: "100%", alignItems: "center", justifyContent: "center", backgroundColor: "#fff" }}
          >
            <CircularProgress size={20} thickness={4} sx={{ color: "hsl(217 91% 55%)" }} />
            <Typography sx={{ fontSize: 12.5, color: "hsl(150 10% 46%)" }}>{textPendingLabel}</Typography>
          </Stack>
        )}
        {active === "text" && (cvText || !textPending) && (
          <Box
            sx={{
              height: "100%", overflow: "auto", p: 2, backgroundColor: "#fff",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12,
              lineHeight: 1.6, color: "hsl(150 30% 12%)", whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}
          >
            {cvText || "No text was extracted from this file."}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CvViewer;
